import React from 'react';
import PropTypes from 'prop-types';

/**
 * A preview of the web chat, drawn with the same shapes the widget itself uses:
 * one ground under the header and the conversation, the identity card floating
 * over it, rounded bubbles, and the input as a single pill.
 *
 * It replaced an exported illustration whose white header bar, square bubbles
 * and underlined input no longer exist in the widget, and whose colour legend
 * relied on eight leader lines happening to line up with the eight inputs beside
 * it - those inputs carry their own labels now.
 */

const styles = {
    frame: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid rgba(9, 24, 58, 0.08)',
        fontSize: '11px',
        lineHeight: '15px',
    },
    header: {
        flexShrink: 0,
        padding: '8px 10px 10px',
    },
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',
    },
    action: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: 'rgba(9, 24, 58, 0.06)',
    },
    actionGroup: {
        display: 'flex',
        gap: '6px',
        marginLeft: 'auto',
    },
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: 'fit-content',
        maxWidth: '100%',
        margin: '0 auto',
        padding: '5px 14px 5px 5px',
        borderRadius: '999px',
        backgroundColor: 'var(--ds-bg-primary)',
        boxShadow: '0 3px 10px rgba(9, 24, 58, 0.10)',
    },
    avatar: {
        position: 'relative',
        width: '26px',
        height: '26px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    online: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: '#22C55E',
        border: '1.5px solid #ffffff',
        boxSizing: 'border-box',
    },
    name: {
        fontSize: '11px',
        fontWeight: 700,
        color: '#09183A',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    body: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '7px',
        padding: '6px 12px 10px',
        overflow: 'hidden',
    },
    quickReplies: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '2px',
    },
    footer: {
        flexShrink: 0,
        padding: '8px 10px 10px',
    },
    inputRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 4px 4px 10px',
        borderRadius: '20px',
        border: '1px solid rgba(9, 24, 58, 0.10)',
    },
    placeholder: {
        flex: 1,
        color: '#A4ADC1',
        fontSize: '11px',
    },
    send: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        flexShrink: 0,
    },
};

const bubble = (background, color, radius) => ({
    maxWidth: '78%',
    padding: '8px 11px',
    borderRadius: radius,
    backgroundColor: background,
    color: color,
    wordBreak: 'break-word',
});

const quickReply = (borderColor, color) => ({
    padding: '5px 11px',
    borderRadius: '999px',
    border: '1px solid ' + borderColor,
    color: color,
    fontSize: '10px',
});

const QUICK_REPLIES = ['Orders & Returns', 'Delivery', 'Complaint'];

const WebChatDemo = ({
                         headerText,
                         logoUrl,
                         headerBackground,
                         bodyBackground,
                         botMessageBackground,
                         userMessageBackground,
                         footerBackground,
                         userTextColor,
                         botTextColor,
                         quickReplyTextColor,
                     }) => (
    <div style={styles.frame}>
        <div style={{...styles.header, backgroundColor: bodyBackground}}>
            <div style={styles.headerBar}>
                <div style={styles.action}/>
                <div style={styles.actionGroup}>
                    <div style={styles.action}/>
                    <div style={styles.action}/>
                </div>
            </div>

            <div style={styles.identity}>
                <div style={{...styles.avatar, backgroundColor: headerBackground}}>
                    {logoUrl && <img src={logoUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'contain'}}/>}
                    <span style={styles.online}/>
                </div>
                <div style={{...styles.name, minWidth: 0}}>{headerText || 'Virtual Assistant'}</div>
            </div>
        </div>

        <div style={{...styles.body, backgroundColor: bodyBackground}}>
            <div style={bubble(botMessageBackground, botTextColor, '14px 14px 14px 4px')}>
                Hello! How can I help you today?
            </div>

            <div style={{...bubble(userMessageBackground, userTextColor, '14px 14px 4px 14px'), alignSelf: 'flex-end'}}>
                I have a question about my order
            </div>

            <div style={bubble(botMessageBackground, botTextColor, '14px 14px 14px 4px')}>
                Sure - what would you like to know?
            </div>

            <div style={styles.quickReplies}>
                {QUICK_REPLIES.map(label => (
                    <span key={label} style={quickReply(userMessageBackground, quickReplyTextColor)}>
                        {label}
                    </span>
                ))}
            </div>
        </div>

        <div style={{...styles.footer, backgroundColor: bodyBackground}}>
            <div style={{...styles.inputRow, backgroundColor: footerBackground}}>
                <span style={styles.placeholder}>Type your message</span>
                <div style={{...styles.send, backgroundColor: headerBackground}}/>
            </div>
        </div>
    </div>
);

WebChatDemo.propTypes = {
    headerText: PropTypes.string,
    headerBackground: PropTypes.string.isRequired,
    bodyBackground: PropTypes.string.isRequired,
    botMessageBackground: PropTypes.string.isRequired,
    userMessageBackground: PropTypes.string.isRequired,
    footerBackground: PropTypes.string.isRequired,
    botTextColor: PropTypes.string.isRequired,
    userTextColor: PropTypes.string.isRequired,
    quickReplyTextColor: PropTypes.string.isRequired,
};

export default WebChatDemo;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/webChatDesignAndConfig/components/WebChatDemo.jsx